import {DateOnly, Datetime, EdgeLocalDate, dates as dateUtils} from '@amicamutual/amica-common';
import {
  format,
  addDays,
  isAfter,
  isBefore,
  isFuture,
  isValid,
  isWithinInterval,
  min,
  startOfDay,
  startOfToday,
  subMonths,
  getYear,
  subDays,
  differenceInDays,
  differenceInMonths,
  differenceInMinutes,
  differenceInCalendarDays,
  differenceInCalendarYears,
  parseISO,
  getDaysInMonth,
  closestTo,
  isLastDayOfMonth,
  subYears,
  endOfDay,
  differenceInYears,
  addYears,
  isEqual,
  lastDayOfMonth,
  addMonths,
  isLeapYear
} from 'date-fns';

import {runIfDefined} from 'utils/data-operations';
import {pipe} from 'utils/pipe';
import {regex} from 'utils/regex';

export {isSameDay, isEqual} from 'date-fns';
export {isValidDateFormat, isValidTimeFormat} from 'utils/regex';

/**
 * @prop date - date string (yyyy-MM-dd)
 * @prop time - time string (hh:mm)
 */
export interface DateTimeStrings {
  date: string;
  time: string;
}

/**
 * Constructs a `Date` object from date and time strings.
 * @param date - date string in 'yyyy-MM-dd' format
 * @param time - time string in 'hh:mm' format
 * @returns A Date object
 */
export const constructDateTime = (date: string, time: string): Date => new Date(`${date.replace(/-/g, '/')} ${time}`);

/**
 * Constructs a `Date` object from a date string.
 * @param date - date string in 'yyyy-MM-dd' format
 * @returns A Date object
 */
export const constructDate = (date: string): Date => new Date(`${date.replace(/-/g, '/')}`);

/**
 * @param datetime - a `Datetime` object
 * @returns An object with `date` and `time` strings
 */
export const convertDatetimeToStrings = (datetime: Datetime): DateTimeStrings => {
  const delimiter = datetime.indexOf('T');
  const date = datetime.substring(0, delimiter);
  const startIndexTime = delimiter + 1;
  const time = datetime.substring(startIndexTime, startIndexTime + 5);
  return {date, time};
};

/**
 * Converts a `Date` object to a date in string format.
 * @param date - Date object
 * @returns - String formatted date (`yyyy-MM-dd`)
 */
export const dateToDateString = (date: Date): string => dateFormat(date, 'yyyy-MM-dd');

/**
 * Converts a `Datetime` object to a `Date` object.
 * @param datetime - Datetime object to convert.
 * @returns Date object or undefined.
 */
export const datetimeToDate = (datetime: Datetime | undefined): Date | undefined =>
  runIfDefined((dt) => {
    const {date, time} = convertDatetimeToStrings(dt);
    return constructDateTime(date, time);
  }, datetime);

/**
 * Converts a date string to Datetime format.
 * @param date - date to be converted
 * @returns Date in Datetime format
 */
export const getDatetimeFromString = (date: string) => dateToDatetime(constructDateTime(date.trim(), '00:00'));

/**
 * Converts a date object to a date string in 'MMM d, yyyy' format
 * @param date - date to be formatted
 */
export const getDateLabel = (date: Date | undefined): string => (date ? dateFormat(date, 'MMM d, yyyy') : '');

/**
 * Formats a date to MM/dd/yyyy
 *
 * Note: Amica doesn't want to implement localized date formatting, so we use
 * this for now instead of `react-intl`.
 *
 * From Joe Alba (https://github.com/mojotech/amica/pull/3164#issuecomment-508277750):
 * > Amica ... says they do not want to support intl currencies, date formats,
 * > or timezones (for display) at this time.
 *
 * @param date - date to be formatted
 */
export const getBrowserFormattedDate = (date: DateOnly | Datetime | Date | undefined): string =>
  date ? dateFormat(date, 'MM/dd/yyyy') : '';

export const getBrowserFormattedDateFromString = (unformattedDate: string) =>
  getBrowserFormattedDate(getDatetimeFromString(unformattedDate));

export const getTomorrowDate = () => addDaysToDate(getCurrentDateEastern(), 1);

/**
 * Given and month and year defaults to last day of month and returns an EdgelocalDate
 * @param year - year to use for last day of month day calculation
 * @param month - month to use for last day of month day calculation
 */
export const defaultDayToLastDayOfMonthEdgeLocalDate = (year: number, month: number) => {
  const day = lastDayOfMonth(new Date(year, month - 1, 1)).getDate();
  return {year: year, month: month - 1, day: day};
};

/**
 * Formats an edge local date to MM/dd/yyyy
 *
 * Note: The month is zero-indexed, with January represented as 0.
 * @param date - date to be formatted
 */
export const getBrowserFormattedEdgeLocalDate = (date: EdgeLocalDate): string =>
  pipe(date, ({year, month, day}) => new Date(year, month, day), getBrowserFormattedDate);

const tzAbbr = () => {
  const dateString = String(getCurrentDateEastern());
  /* eslint-disable no-useless-escape */
  const abbr =
    // Works for the majority of modern browsers
    dateString.match(/\(([^\)]+)\)$/) ||
    // IE outputs date strings in a different format:
    dateString.match(/([A-Z]+) [\d]{4}$/);
  /* eslint-enable no-useless-escape */

  if (abbr) {
    // Old Firefox uses the long timezone name (e.g., "Central
    // Daylight Time" instead of "CDT")
    const letters = abbr[1].match(/[A-Z]/g);
    if (letters !== null) {
      if (letters.length === 3) {
        return letters[0].concat(letters[2]);
      }
      return letters.join('');
    }
  }

  // Return a GMT offset for browsers
  // that don't include the user's zone abbreviation (e.g.,
  // "GMT-0500".)
  if (!abbr && regex.GMT_OFFSET.test(dateString)) {
    return RegExp.$1;
  }

  return tzAbbr;
};

/**
 * Formats a date to MM/dd/yyyy
 * @param date - date to be formatted
 */
export const getBrowserFormattedDatetime = (date: Datetime | Date | undefined): string =>
  date ? dateFormat(date, 'MM/dd/yyyy, h:mm aaaa ') + tzAbbr() : '';

/**
 * Converts a date object to a time string in 'h:mm A' format
 * @param date - date to be formatted
 */
export const getTimeLabel = (date: Date | undefined): string => (date ? dateFormat(date, 'h:mm aaaa') : '');

/**
 * Adds the given number of days to the provided Date
 * @param date - date string to be modified
 * @param days - the number of days to add
 */
export const addDaysToDate = (date: Date | DateOnly, days: number): DateOnly =>
  dateToDateOnly(addDays(toDate(date), days));

/**
 * Adds the given number of months to the provided Date
 * @param date - date string to be modified
 * @param months - the number of months to add
 */
export const addMonthsToDate = (date: Date | DateOnly, months: number): DateOnly =>
  dateToDateOnly(addMonths(toDate(date), months));

/**
 * Adds the given number of years to the provided Date
 * @param date - date string to be modified
 * @param years - the number of years to add
 */
export const addYearsToDate = (date: Date | DateOnly, years: number): DateOnly =>
  dateToDateOnly(addYears(toDate(date), years));

/**
 * Converts an EdgeLocalDate to DateOnly format
 * @param date - EdgeLocalDate to be formatted
 */
export const edgeLocalDateToDateOnly = ({year, month, day}: EdgeLocalDate): DateOnly =>
  dateToDateOnly(new Date(year, month, day));

/**
 * Converts a valid date string to an EdgeLocalDate
 * @param date - string to be formatted
 */
export const stringToEdgeLocalDate = (dateString: string): EdgeLocalDate => {
  const date = constructDate(dateString);
  return {year: date.getFullYear(), month: date.getMonth(), day: date.getDate()};
};

/**
 * TODO: Retrieve a current timestamp from server
 */
const getCurrentDate = () => new Date();

export const getCurrentDateEastern = () => convertDateToEastern(getCurrentDate());

export const convertDateToEastern = (date: Date) => {
  const conversionDate = date;
  const convertToLocalString = conversionDate.toLocaleString('en-US', {timeZone: 'America/New_York'});
  const convertedDate = new Date(convertToLocalString);

  return convertedDate;
};

export const toDate = (date: Date | DateOnly | Datetime | string | number) => {
  if (isDateOnly(date)) {
    return parseISO(date);
  }
  if (isDate(date)) {
    return date;
  }
  return new Date(date);
};

export const getCurrentMonthYearEastern = () => {
  const currentDateEastern = getCurrentDateEastern();
  return {
    month: currentDateEastern.getMonth() + 1, // js months are indexed at 0
    year: currentDateEastern.getFullYear()
  };
};

export const getCurrentDatetimeEastern = () => dateToDatetime(getCurrentDateEastern());

export const getCurrentDateOnlyEastern = () => dateToDateOnly(getCurrentDateEastern());

export const isWithinRange = (testDate: Date, beginDate: Date | DateOnly, endDate: Date | DateOnly) =>
  isWithinInterval(testDate, {start: toDate(beginDate), end: toDate(endDate)});

export const isWithinRangeInclusive = (testDate: Date, beginDate: string, endDate: string) => {
  const formattedEndDate = isDateOnly(endDate) ? endOfDay(toDate(endDate)) : endDate;

  return isWithinInterval(testDate, {start: toDate(beginDate), end: toDate(formattedEndDate)});
};

export const isWithinRecurringTimeRange = (startTime: string, endTime: string, startDay: string, endDay: string) => {
  const currentDateTime = getCurrentDateEastern();
  const currentDay = currentDateTime.getDay();
  const distanceFromStartDay = +startDay - currentDay;
  const startDate = addDays(currentDateTime, distanceFromStartDay);

  const currentFromDate = startDate.getDay();
  const distanceFromEndDay = (+endDay + 7 - currentFromDate) % 7;
  const endDate = addDays(startDate, distanceFromEndDay);

  const fromDateTime = constructDateTime(dateToDateString(startDate), startTime);
  const toDateTime = constructDateTime(dateToDateString(endDate), endTime);

  return isWithinRange(currentDateTime, fromDateTime, toDateTime);
};

export const minDate = (incomingArray: (Date | DateOnly)[]) => {
  const dateArray = incomingArray.map((date) => toDate(date));
  return min(dateArray);
};

export const subtractDays = (date: Date | number | string, amount: number) => subDays(toDate(date), amount);

export const subtractMonths = (date: Date | number | string, amount: number) => subMonths(toDate(date), amount);

export const dateFormat = (date: Date | Datetime | DateOnly | number, formatString: string, options?: {}) =>
  format(toDate(date), formatString, options);

export const getDateYear = (date: number | Date | Datetime) => getYear(toDate(date));

export const diffInDays = (laterDate: Date | number | string, earlierDate: Date | number | string) =>
  differenceInDays(toDate(laterDate), toDate(earlierDate));

export const diffInCalendarDays = (laterDate: Date | number | string, earlierDate: Date | number | string) =>
  differenceInCalendarDays(toDate(laterDate), toDate(earlierDate));

export const diffInCalendarYears = (laterDate: Date | number | string, earlierDate: Date | number | string) =>
  differenceInCalendarYears(toDate(laterDate), toDate(earlierDate));

export const diffInYears = (laterDate: Date | number | string, earlierDate: Date | number | string) =>
  differenceInYears(toDate(laterDate), toDate(earlierDate));

export const diffInMinutes = (laterDate: Date | number | string, earlierDate: Date | number | string) =>
  differenceInMinutes(toDate(laterDate), toDate(earlierDate));

export const diffInMonths = (laterDate: Date | number | string, earlierDate: Date | number | string) =>
  differenceInMonths(toDate(laterDate), toDate(earlierDate));

export const isAfterDate = (
  date: DateOnly | Date | number | string,
  dateToCompare: DateOnly | Date | number | string
) => isAfter(toDate(date), toDate(dateToCompare));

export const isBeforeDate = (
  date: DateOnly | Date | number | string,
  dateToCompare: DateOnly | Date | number | string
) => isBefore(toDate(date), toDate(dateToCompare));

export const isEqualOrBeforeDate = (
  date: DateOnly | Date | number | string,
  dateToCompare: DateOnly | Date | number | string
) => isBefore(toDate(date), toDate(dateToCompare)) || isEqual(toDate(date), toDate(dateToCompare));

export const isDateInFuture = (date: DateOnly | Date | number | string) => isFuture(toDate(date));

export const todayStartTime = () => startOfToday();

export const dayStartTime = (day: Date) => startOfDay(day);

export const isValidDate = (date: any) => isValid(date);

export const getTotalDaysInMonth = (date: Date | number) => getDaysInMonth(date);

/**
 * Typeguard that ensures a `date-only` value is a `string` and correctly formatted.
 */
const isDateOnly = (dateOnly: any): dateOnly is DateOnly =>
  typeof dateOnly === 'string' && regex.DATE_ONLY.test(dateOnly);

export const isDatetime = (datetime: any): datetime is Datetime => dateUtils.isDatetime(datetime);

const isDate = (date: any): date is Date => date instanceof Date;

export const closestDate = (dateToCompare: Date | number, datesArray: (Date | number)[]) =>
  closestTo(dateToCompare, datesArray);

export const isDayLastOfMonth = (currentDate: Date | number) => isLastDayOfMonth(currentDate);

export const subtractYears = (currentDate: Date | number, numberOfYears: number) =>
  subYears(currentDate, numberOfYears);

/*
 * Non date-fns version of isValidDate
 */
export const isValidDateObject = (date?: Date) => {
  if (!date) {
    return false;
  }
  return !isNaN(date.getTime());
};

/**
 * Compares a year, month, day to an EdgeLocalDate
 * @param year - year of date to be compared
 * @param month - month of date to be compared
 * @param day - day of date to be compared
 * @param edgeLocalDate - EdgeLocal date to be compared
 */
export const isDateEqualToEdgeLocalDate = (year: number, month: number, day: number, edgeLocalDate: EdgeLocalDate) => {
  const edgeDate = new Date(edgeLocalDate.year, edgeLocalDate.month, edgeLocalDate.day);
  const compareDate = new Date(year, month, day);
  return isEqual(edgeDate, compareDate);
};

export const isLeapDay = (dateToCheck: Date) =>
  isLeapYear(dateToCheck) && dateToCheck.getMonth() === 1 && dateToCheck.getDate() === 29;

/** Convert a JavaScript `Date` to a `Datetime` string */
export const dateToDatetime = (date: Date, dateFmt?: string): Datetime =>
  format(date, dateFmt ? dateFmt : "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") as Datetime;

/**
 * Convert a `Date` object to RAML's "date-only" format (e.g. `2017-09-12`)
 * @param date - A `Date` instance
 */
export const dateToDateOnly = (date: Date): DateOnly => format(date, 'yyyy-MM-dd') as DateOnly;
